import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { BaseUrl } from '../../config'; // Adjust the import path as needed

export default function QuickBuyPaymentStatus() {
  const baseUrl = process.env.REACT_APP_API;  
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('processing');
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);
  const invoiceId = searchParams.get('invoice_id');

  const getExplorerUrl = (txHash, chain) => {
    if (chain === 'SOL') {
      return `https://solscan.io/tx/${txHash}`;
    } else {
      return `https://etherscan.io/tx/${txHash}`;
    }
  };

  useEffect(() => {
    const checkStatus = async () => {
      if (!invoiceId) {
        setError('Missing invoice ID');
        setChecking(false);
        return;
      }

      try {
        const response = await axios.get(`${baseUrl}/api/quick-payment/status?invoice_id=${invoiceId}`);
        const data = response.data;
        if (data?.success && !data?.transaction?.txid_in) {
          navigate('/token/quick-buy/payment/failed');
         return;
        }

        if (data?.success && data.status === 'Completed' && data.transaction?.status === 'Completed') {
          setStatus('completed');
          setChecking(false);
          
          // Redirect to success page with transaction details
          navigate(`/token/payment/success?amount=${data.transaction?.amount}&currency=${data.transaction?.currency}`);

        } else if (data.status === 'Canceled') {
          setStatus('failed');
          setChecking(false);
          setError('Payment failed. Please try again.');
          
          // Redirect to failure page after delay
          setTimeout(() => {
            navigate('/token/quick-buy/payment/failed');
          }, 3000);
        }
      } catch (error) {
        setChecking(false);
        setStatus('error');
        setError('Error verifying payment status. Please check your transaction history.');
      }
    };

    const interval = setInterval(() => {
      if (checking) {
        checkStatus();
      }
    }, 5000); // Check every 5 seconds

    // Initial check
    checkStatus();

    return () => clearInterval(interval);
  }, [invoiceId, checking, navigate]);

  const getStatusContent = () => {
    switch (status) {
      case 'completed':
        return {
          icon: (
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#00ff9d" strokeWidth="2"/>
              <path d="M8 12L11 15L16 9" stroke="#00ff9d" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ),
          title: 'Payment Completed!',
          message: 'Your transaction was successful. Redirecting...',
          color: '#00ff9d'
        };
      case 'failed':
        return {
          icon: (
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#ff4d4f" strokeWidth="2"/>
              <path d="M15 9L9 15M9 9L15 15" stroke="#ff4d4f" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ),
          title: 'Payment Failed',
          message: error || 'The payment could not be processed.',
          color: '#ff4d4f'
        };
      case 'error':
        return {
          icon: (
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#faad14" strokeWidth="2"/>
              <path d="M12 8V12M12 16H12.01" stroke="#faad14" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ),
          title: 'Verification Error',
          message: error || 'Could not verify payment status.',
          color: '#faad14'
        };
      default:
        return {
          icon: (
            <div className="loader" style={{
              width: '80px',
              height: '80px',
              margin: '0 auto',
              border: '8px solid rgba(0, 184, 186, 0.2)',
              borderTop: '8px solid #00b8ba',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          ),
          title: 'Processing Payment',
          message: 'Please wait while we verify your transaction...',
          color: '#00b8ba'
        };
    }
  };

  const statusContent = getStatusContent();

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
      padding: '20px',
      fontFamily: '"Inter", sans-serif'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        padding: '40px 30px',
        width: '100%',
        maxWidth: '450px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        textAlign: 'center'
      }}>
        {/* Status icon */}
        <div style={{
          marginBottom: '30px',
          color: statusContent.color
        }}>
          {statusContent.icon}
        </div>
        
        {/* Status message */}
        <h1 style={{
          color: '#fff',
          fontSize: '28px',
          fontWeight: '700',
          marginBottom: '10px'
        }}>
          {statusContent.title}
        </h1>
        
        <p style={{
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '16px',
          marginBottom: '30px'
        }}>
          {statusContent.message}
        </p>

        {/* Additional info for processing state */}
        {status === 'processing' && (
          <div style={{
            background: 'rgba(0, 184, 186, 0.1)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '30px',
            border: '1px solid rgba(0, 184, 186, 0.2)'
          }}>
            <p style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px',
              margin: 0
            }}>
              This may take a few minutes. Please don't close this page.
            </p>
          </div>
        )}

        {/* CSS for the loader animation */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}